﻿import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@pms/shared';
import { CheckCircle2, CheckSquare, ClipboardCheck, Users } from 'lucide-react';
import { usePermission } from '@/hooks/usePermission';
import { StatCard } from '@/components/ui/stat-card';
import { usersService } from '@/services/users.service';
import { attendanceService } from '@/services/attendance.service';
import { leaveService } from '@/services/leave.service';
import { penaltyService } from '@/services/penalty.service';
import { tasksService } from '@/services/tasks.service';

const todayISO = new Date().toISOString().split('T')[0]!;

function KpiSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-[100px] animate-pulse rounded-xl bg-muted/50" />
      ))}
    </div>
  );
}

export function AdminKPIs() {
  const { has } = usePermission();

  const staffQ = useQuery({
    queryKey: ['dashboard', 'admin-kpi', 'staff-count'],
    queryFn: () => usersService.list({ page: 1, limit: 1 }),
    enabled: has(PERMISSIONS.STAFF_VIEW_ALL),
  });

  const attendanceSummaryQ = useQuery({
    queryKey: ['dashboard', 'admin-kpi', 'attendance-summary', todayISO],
    queryFn: () => attendanceService.getDailySummary(todayISO),
    enabled: has(PERMISSIONS.ATTENDANCE_VIEW_ALL),
  });

  const pendingLeaveQ = useQuery({
    queryKey: ['dashboard', 'admin-kpi', 'pending-leave'],
    queryFn: () => leaveService.listTeamRequests({ status: 'pending', page: 1, limit: 1 }),
    enabled: has(PERMISSIONS.LEAVE_APPROVE),
  });

  const pendingPenaltyQ = useQuery({
    queryKey: ['dashboard', 'admin-kpi', 'pending-penalty'],
    queryFn: () => penaltyService.list({ status: 'pending', page: 1, limit: 1 }),
    enabled: has(PERMISSIONS.PENALTY_MANAGE),
  });

  const tasksQ = useQuery({
    queryKey: ['dashboard', 'admin-kpi', 'tasks-count'],
    queryFn: () => tasksService.list({ page: 1, limit: 1 }),
    enabled: has(PERMISSIONS.TASK_VIEW_ALL),
  });

  const isLoading =
    staffQ.isLoading ||
    attendanceSummaryQ.isLoading ||
    pendingLeaveQ.isLoading ||
    tasksQ.isLoading;

  if (isLoading) return <KpiSkeleton />;

  const staffCount = staffQ.data?.pagination?.total ?? '—';
  const presentCount = attendanceSummaryQ.data?.present ?? '—';
  const leavePending = pendingLeaveQ.data?.pagination?.total ?? 0;
  const penaltyPending = pendingPenaltyQ.data?.pagination?.total ?? 0;
  const totalPending = leavePending + penaltyPending;
  const openTasks = tasksQ.data?.pagination?.total ?? '—';

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard
        label="Active Staff"
        value={staffCount}
        hint="Total employees"
        icon={Users}
        tone="teal"
      />
      <StatCard
        label="Present Today"
        value={presentCount}
        hint={`As of ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`}
        icon={CheckCircle2}
        tone="emerald"
      />
      <StatCard
        label="Pending Approvals"
        value={totalPending}
        hint="Leave + penalty"
        icon={ClipboardCheck}
        tone="amber"
      />
      <StatCard
        label="Open Tasks"
        value={openTasks}
        hint="All active tasks"
        icon={CheckSquare}
        tone="violet"
      />
    </div>
  );
}
